import { Intersection, Point } from '@imagise/shared';
import type {
  TDegree,
  TOriginX,
  TOriginY,
  TCornerPoint,
  TBBox,
  TMat2D,
  GeometryProps,
  TMatrixCache,
  TransformOptions,
} from './types';
// Direct file import instead of the '../core' barrel: the barrel pulls static-canvas →
// editor-object → object-geometry and closes a module cycle.
import { CommonMethods } from '../core/CommonMethods';

/**
 * Identity matrix constant
 */
const IDENTITY_MATRIX: TMat2D = [1, 0, 0, 1, 0, 0];

/**
 * Origin constants
 */
const CENTER = 'center';
const LEFT = 'left';
const RIGHT = 'right';
const TOP = 'top';
const BOTTOM = 'bottom';

/**
 * ObjectGeometry - Base class for canvas objects with geometric transformations
 *
 * Extends CommonMethods with geometric calculations including:
 * - Position, size, and transformations (rotation, scale, skew)
 * - Bounding box calculations
 * - Coordinate transformations
 * - Intersection detection
 * - Matrix transformations
 *
 * This class is SSR-compatible and doesn't use browser-specific APIs.
 *
 * @template EventSpec - Event specification for type-safe event handling
 *
 * @example
 * ```typescript
 * interface MyObjectEvents {
 *   'modified': { target: MyObject };
 * }
 *
 * class MyObject extends ObjectGeometry<MyObjectEvents> {
 *   constructor() {
 *     super();
 *     this.width = 100;
 *     this.height = 100;
 *   }
 * }
 *
 * const obj = new MyObject();
 * obj.setPosition(50, 50);
 * obj.scale(2);
 * const bbox = obj.getBoundingRect();
 * ```
 */
/**
 * Geometric contract for pairwise object comparisons.
 * It does not depend on the EventSpec generic, so objects with different event
 * dictionaries (EventBus is invariant in EventSpec) remain comparable.
 */
export type GeometryComparable = {
  getCoords(): Point[];
  containsPoint(point: Point): boolean;
  isContainedWithinObject(other: GeometryComparable): boolean;
};

export class ObjectGeometry<EventSpec = Record<string, unknown>>
  extends CommonMethods<EventSpec>
  implements Partial<GeometryProps>
{
  // Geometric properties
  left = 0;
  top = 0;
  width = 100;
  height = 100;
  scaleX = 1;
  scaleY = 1;
  angle: TDegree = 0;
  skewX = 0;
  skewY = 0;
  flipX = false;
  flipY = false;
  originX: TOriginX = CENTER;
  originY: TOriginY = CENTER;
  strokeWidth = 1;
  strokeUniform = false;
  padding = 0;

  /**
   * Cached corner coordinates
   * Updated via setCoords()
   */
  protected aCoords?: TCornerPoint;

  /**
   * Cache for own transform matrix
   */
  protected ownMatrixCache?: TMatrixCache;

  /**
   * Cache for full transform matrix (including parent)
   */
  protected matrixCache?: TMatrixCache;

  /**
   * Parent group reference (for nested transformations)
   */
  protected group?: ObjectGeometry;

  // #region Position Getters/Setters

  /**
   * Gets X position according to object's originX property
   * @returns X coordinate
   */
  getX(): number {
    return this.getXY().x;
  }

  /**
   * Sets X position according to object's originX property
   * @param value - X coordinate
   */
  setX(value: number): void {
    this.setXY(this.getXY().setX(value));
  }

  /**
   * Gets Y position according to object's originY property
   * @returns Y coordinate
   */
  getY(): number {
    return this.getXY().y;
  }

  /**
   * Sets Y position according to object's originY property
   * @param value - Y coordinate
   */
  setY(value: number): void {
    this.setXY(this.getXY().setY(value));
  }

  /**
   * Gets relative X position (in parent's coordinate space)
   * @returns X coordinate
   */
  getRelativeX(): number {
    return this.left;
  }

  /**
   * Sets relative X position
   * @param value - X coordinate
   */
  setRelativeX(value: number): void {
    this.left = value;
  }

  /**
   * Gets relative Y position (in parent's coordinate space)
   * @returns Y coordinate
   */
  getRelativeY(): number {
    return this.top;
  }

  /**
   * Sets relative Y position
   * @param value - Y coordinate
   */
  setRelativeY(value: number): void {
    this.top = value;
  }

  /**
   * Gets absolute XY position (in canvas coordinate space)
   * @returns Point with absolute coordinates
   */
  getXY(): Point {
    const relativePosition = this.getRelativeXY();
    if (this.group) {
      return this.transformPoint(relativePosition, this.group.calcTransformMatrix());
    }
    return relativePosition;
  }

  /**
   * Sets absolute XY position
   * @param point - Target position
   * @param originX - Horizontal origin (optional)
   * @param originY - Vertical origin (optional)
   */
  setXY(point: Point, originX?: TOriginX, originY?: TOriginY): void {
    if (this.group) {
      point = this.transformPoint(point, this.invertTransform(this.group.calcTransformMatrix()));
    }
    this.setRelativeXY(point, originX, originY);
  }

  /**
   * Gets relative XY position
   * @returns Point with relative coordinates
   */
  getRelativeXY(): Point {
    return new Point(this.left, this.top);
  }

  /**
   * Sets relative XY position
   * @param point - Target position
   * @param originX - Horizontal origin
   * @param originY - Vertical origin
   */
  setRelativeXY(point: Point, originX: TOriginX = this.originX, originY: TOriginY = this.originY): void {
    this.setPositionByOrigin(point, originX, originY);
  }

  // #endregion

  // #region Coordinate Calculations

  /**
   * Gets the four corner coordinates of the object
   * @returns Array of corner points [tl, tr, br, bl]
   */
  getCoords(): Point[] {
    const coords = this.aCoords || this.calcACoords();
    const { tl, tr, br, bl } = coords;
    const points = [tl, tr, br, bl];

    if (this.group) {
      const t = this.group.calcTransformMatrix();
      return points.map(p => this.transformPoint(p, t));
    }

    return points;
  }

  /**
   * Calculates absolute corner coordinates
   * @returns Corner points object
   */
  calcACoords(): TCornerPoint {
    const rotateMatrix = this.createRotateMatrix(this.angle);
    const center = this.getRelativeCenterPoint();
    const translateMatrix = this.createTranslateMatrix(center.x, center.y);
    const finalMatrix = this.multiplyMatrices(translateMatrix, rotateMatrix);
    const dim = this._getTransformedDimensions();
    const w = dim.x / 2;
    const h = dim.y / 2;

    return {
      tl: this.transformPoint({ x: -w, y: -h }, finalMatrix),
      tr: this.transformPoint({ x: w, y: -h }, finalMatrix),
      bl: this.transformPoint({ x: -w, y: h }, finalMatrix),
      br: this.transformPoint({ x: w, y: h }, finalMatrix),
    };
  }

  /**
   * Updates corner coordinates cache
   * Should be called after transformations
   */
  setCoords(): void {
    this.aCoords = this.calcACoords();
  }

  /**
   * Gets bounding rectangle
   * @returns Bounding box with left, top, width, height
   */
  getBoundingRect(): TBBox {
    const coords = this.getCoords();
    return this.makeBoundingBoxFromPoints(coords);
  }

  /**
   * Creates bounding box from array of points
   * @param points - Array of points
   * @returns Bounding box
   */
  protected makeBoundingBoxFromPoints(points: Point[]): TBBox {
    const xPoints = points.map(p => p.x);
    const yPoints = points.map(p => p.y);

    const minX = Math.min(...xPoints);
    const maxX = Math.max(...xPoints);
    const minY = Math.min(...yPoints);
    const maxY = Math.max(...yPoints);

    return {
      left: minX,
      top: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }

  // #endregion

  // #region Dimensions

  /**
   * Gets scaled width
   * @returns Width after transformations
   */
  getScaledWidth(): number {
    return this._getTransformedDimensions().x;
  }

  /**
   * Gets scaled height
   * @returns Height after transformations
   */
  getScaledHeight(): number {
    return this._getTransformedDimensions().y;
  }

  /**
   * Gets transformed dimensions based on scale and skew
   * @param options - Optional override options
   * @returns Point with transformed width and height
   */
  protected _getTransformedDimensions(options: Partial<GeometryProps> = {}): Point {
    const scaleX = options.scaleX ?? this.scaleX;
    const scaleY = options.scaleY ?? this.scaleY;
    const skewX = options.skewX ?? this.skewX;
    const skewY = options.skewY ?? this.skewY;
    const strokeWidth = options.strokeWidth ?? this.strokeWidth;

    let preScalingStroke = strokeWidth;
    let postScalingStroke = 0;

    if (this.strokeUniform) {
      preScalingStroke = 0;
      postScalingStroke = strokeWidth;
    }

    const dimX = this.width + preScalingStroke;
    const dimY = this.height + preScalingStroke;

    // Simple case: no skew
    if (skewX === 0 && skewY === 0) {
      return new Point(dimX * Math.abs(scaleX), dimY * Math.abs(scaleY)).scalarAdd(postScalingStroke);
    }

    // Complex case: with skew
    const skewXRad = this.degreesToRadians(skewX);
    const skewYRad = this.degreesToRadians(skewY);

    const transformedWidth =
      Math.abs(dimX * scaleX * Math.cos(skewYRad)) + Math.abs(dimY * scaleY * Math.sin(skewXRad));
    const transformedHeight =
      Math.abs(dimX * scaleX * Math.sin(skewYRad)) + Math.abs(dimY * scaleY * Math.cos(skewXRad));

    return new Point(transformedWidth, transformedHeight).scalarAdd(postScalingStroke);
  }

  // #endregion

  // #region Scaling

  /**
   * Scales object equally by x and y
   * @param value - Scale factor
   */
  scale(value: number): void {
    this.scaleX = value;
    this.scaleY = value;
    this.setCoords();
  }

  /**
   * Scales object to given width
   * @param value - Target width
   */
  scaleToWidth(value: number): void {
    const boundingRectFactor = this.getBoundingRect().width / this.getScaledWidth();
    this.scale(value / this.width / boundingRectFactor);
  }

  /**
   * Scales object to given height
   * @param value - Target height
   */
  scaleToHeight(value: number): void {
    const boundingRectFactor = this.getBoundingRect().height / this.getScaledHeight();
    this.scale(value / this.height / boundingRectFactor);
  }

  // #endregion

  // #region Positioning

  /**
   * Sets position by specific origin point
   * @param pos - Target position
   * @param originX - Horizontal origin
   * @param originY - Vertical origin
   */
  setPositionByOrigin(pos: Point, originX: TOriginX, originY: TOriginY): void {
    const center = this.translateToCenterPoint(pos, originX, originY);
    const position = this.translateToOriginPoint(center, this.originX, this.originY);
    this.set({ left: position.x, top: position.y });
  }

  /**
   * Gets center point in absolute coordinates
   * @returns Center point
   */
  getCenterPoint(): Point {
    const relCenter = this.getRelativeCenterPoint();
    if (this.group) {
      return this.transformPoint(relCenter, this.group.calcTransformMatrix());
    }
    return relCenter;
  }

  /**
   * Gets center point in relative coordinates
   * @returns Center point
   */
  getRelativeCenterPoint(): Point {
    return this.translateToCenterPoint(new Point(this.left, this.top), this.originX, this.originY);
  }

  /**
   * Translates point to center coordinates
   * @param point - Source point
   * @param originX - Horizontal origin
   * @param originY - Vertical origin
   * @returns Translated point
   */
  translateToCenterPoint(point: Point, originX: TOriginX, originY: TOriginY): Point {
    if (originX === CENTER && originY === CENTER) {
      return point;
    }

    const p = this.translateToGivenOrigin(point, originX, originY, CENTER, CENTER);

    if (this.angle) {
      return p.rotate(this.degreesToRadians(this.angle), point);
    }

    return p;
  }

  /**
   * Translates point from center to specific origin
   * @param center - Center point
   * @param originX - Target horizontal origin
   * @param originY - Target vertical origin
   * @returns Translated point
   */
  translateToOriginPoint(center: Point, originX: TOriginX, originY: TOriginY): Point {
    const p = this.translateToGivenOrigin(center, CENTER, CENTER, originX, originY);

    if (this.angle) {
      return p.rotate(this.degreesToRadians(this.angle), center);
    }

    return p;
  }

  /**
   * Translates point from one origin to another
   * @param point - Source point
   * @param fromOriginX - Source horizontal origin
   * @param fromOriginY - Source vertical origin
   * @param toOriginX - Target horizontal origin
   * @param toOriginY - Target vertical origin
   * @returns Translated point
   */
  translateToGivenOrigin(
    point: Point,
    fromOriginX: TOriginX,
    fromOriginY: TOriginY,
    toOriginX: TOriginX,
    toOriginY: TOriginY
  ): Point {
    let x = point.x;
    let y = point.y;

    const offsetX = this.resolveOrigin(toOriginX) - this.resolveOrigin(fromOriginX);
    const offsetY = this.resolveOrigin(toOriginY) - this.resolveOrigin(fromOriginY);

    if (offsetX || offsetY) {
      const dim = this._getTransformedDimensions();
      x += offsetX * dim.x;
      y += offsetY * dim.y;
    }

    return new Point(x, y);
  }

  // #endregion

  // #region Matrix Operations

  /**
   * Calculates full transform matrix including parent transformations
   * @param skipGroup - If true, doesn't include parent group transformation
   * @returns Transform matrix
   */
  calcTransformMatrix(skipGroup = false): TMat2D {
    let matrix = this.calcOwnMatrix();

    if (skipGroup || !this.group) {
      return matrix;
    }

    const key = this.transformMatrixKey(skipGroup);
    const cache = this.matrixCache;

    if (cache && cache.key.every((x, i) => x === key[i])) {
      return cache.value;
    }

    if (this.group) {
      matrix = this.multiplyMatrices(this.group.calcTransformMatrix(false), matrix);
    }

    this.matrixCache = { key, value: matrix };
    return matrix;
  }

  /**
   * Calculates object's own transform matrix
   * @returns Transform matrix
   */
  calcOwnMatrix(): TMat2D {
    const key = this.transformMatrixKey(true);
    const cache = this.ownMatrixCache;

    if (cache && this.arrayEquals(cache.key, key)) {
      return cache.value;
    }

    const center = this.getRelativeCenterPoint();
    const value = this.composeMatrix({
      angle: this.angle,
      translateX: center.x,
      translateY: center.y,
      scaleX: this.scaleX,
      scaleY: this.scaleY,
      skewX: this.skewX,
      skewY: this.skewY,
      flipX: this.flipX,
      flipY: this.flipY,
    });

    this.ownMatrixCache = { key, value };
    return value;
  }

  /**
   * Creates cache key for transform matrix
   * @param skipGroup - If true, doesn't include parent group in key
   * @returns Array of numbers representing current transform state
   */
  transformMatrixKey(skipGroup = false): number[] {
    let prefix: number[] = [];

    if (!skipGroup && this.group) {
      prefix = this.group.transformMatrixKey(skipGroup);
    }

    prefix.push(
      this.top,
      this.left,
      this.width,
      this.height,
      this.scaleX,
      this.scaleY,
      this.angle,
      this.strokeWidth,
      this.skewX,
      this.skewY,
      +this.flipX,
      +this.flipY,
      this.resolveOrigin(this.originX),
      this.resolveOrigin(this.originY)
    );

    return prefix;
  }

  // #endregion

  // #region Intersection Detection

  /**
   * Checks if object intersects with a rectangle
   * @param tl - Top-left point of rectangle
   * @param br - Bottom-right point of rectangle
   * @returns True if intersects
   */
  intersectsWithRect(tl: Point, br: Point): boolean {
    const coords = this.getCoords();
    return this.polygonIntersectsWithRect(coords, tl, br);
  }

  /**
   * Checks if object intersects with another object
   * @param other - Other object to test
   * @returns True if intersects
   */
  intersectsWithObject(other: GeometryComparable): boolean {
    const thisCoords = this.getCoords();
    const otherCoords = other.getCoords();

    return (
      this.polygonIntersectsWithPolygon(thisCoords, otherCoords) ||
      other.isContainedWithinObject(this) ||
      this.isContainedWithinObject(other)
    );
  }

  /**
   * Checks if this object is fully contained within another object
   * @param other - Other object to test
   * @returns True if fully contained
   */
  isContainedWithinObject(other: GeometryComparable): boolean {
    const points = this.getCoords();
    return points.every(point => other.containsPoint(point));
  }

  /**
   * Checks if this object is fully contained within a rectangle
   * @param tl - Top-left point
   * @param br - Bottom-right point
   * @returns True if fully contained
   */
  isContainedWithinRect(tl: Point, br: Point): boolean {
    const { left, top, width, height } = this.getBoundingRect();
    return left >= tl.x && left + width <= br.x && top >= tl.y && top + height <= br.y;
  }

  /**
   * Checks if point is inside object
   * @param point - Point to check
   * @returns True if point is inside
   */
  containsPoint(point: Point): boolean {
    const coords = this.getCoords();
    return Intersection.isPointInPolygon(point, coords);
  }

  // #endregion

  // #region Utility Methods

  /**
   * Converts degrees to radians
   * @param degrees - Angle in degrees
   * @returns Angle in radians
   */
  protected degreesToRadians(degrees: number): number {
    return (degrees * Math.PI) / 180;
  }

  /**
   * Resolves origin string to numeric value
   * @param origin - Origin string
   * @returns Numeric value (0, 0.5, or 1)
   */
  protected resolveOrigin(origin: TOriginX | TOriginY): number {
    if (origin === LEFT || origin === TOP) return 0;
    if (origin === CENTER) return 0.5;
    if (origin === RIGHT || origin === BOTTOM) return 1;
    return 0.5;
  }

  /**
   * Creates rotation matrix
   * @param angle - Angle in degrees
   * @returns Transform matrix
   */
  protected createRotateMatrix(angle: number): TMat2D {
    const theta = this.degreesToRadians(angle);
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    return [cos, sin, -sin, cos, 0, 0];
  }

  /**
   * Creates translation matrix
   * @param x - X translation
   * @param y - Y translation
   * @returns Transform matrix
   */
  protected createTranslateMatrix(x: number, y: number): TMat2D {
    return [1, 0, 0, 1, x, y];
  }

  /**
   * Composes transformation matrix from options
   * @param options - Transform options
   * @returns Transform matrix
   */
  protected composeMatrix(options: TransformOptions): TMat2D {
    const {
      angle = 0,
      translateX = 0,
      translateY = 0,
      scaleX = 1,
      scaleY = 1,
      skewX = 0,
      skewY = 0,
      flipX = false,
      flipY = false,
    } = options;

    const angleRad = this.degreesToRadians(angle);
    const skewXRad = this.degreesToRadians(skewX);
    const skewYRad = this.degreesToRadians(skewY);

    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);

    const sx = scaleX * (flipX ? -1 : 1);
    const sy = scaleY * (flipY ? -1 : 1);

    const a = cos * sx;
    const b = sin * sx;
    const c = -sin * sy;
    const d = cos * sy;

    // Apply skew
    const skewMatrix: TMat2D = [
      1 + Math.tan(skewYRad) * Math.tan(skewXRad),
      Math.tan(skewYRad),
      Math.tan(skewXRad),
      1,
      0,
      0,
    ];

    let result: TMat2D = [a, b, c, d, translateX, translateY];

    if (skewX !== 0 || skewY !== 0) {
      result = this.multiplyMatrices(result, skewMatrix);
    }

    return result;
  }

  /**
   * Multiplies two transformation matrices
   * @param a - First matrix
   * @param b - Second matrix
   * @returns Result matrix
   */
  protected multiplyMatrices(a: TMat2D, b: TMat2D): TMat2D {
    return [
      a[0] * b[0] + a[2] * b[1],
      a[1] * b[0] + a[3] * b[1],
      a[0] * b[2] + a[2] * b[3],
      a[1] * b[2] + a[3] * b[3],
      a[0] * b[4] + a[2] * b[5] + a[4],
      a[1] * b[4] + a[3] * b[5] + a[5],
    ];
  }

  /**
   * Inverts a transformation matrix
   * @param matrix - Matrix to invert
   * @returns Inverted matrix
   */
  protected invertTransform(matrix: TMat2D): TMat2D {
    const [a, b, c, d, e, f] = matrix;
    const determinant = a * d - b * c;

    if (determinant === 0) {
      return [...IDENTITY_MATRIX];
    }

    return [
      d / determinant,
      -b / determinant,
      -c / determinant,
      a / determinant,
      (c * f - d * e) / determinant,
      (b * e - a * f) / determinant,
    ];
  }

  /**
   * Transforms point by matrix
   * @param point - Point to transform
   * @param matrix - Transform matrix
   * @returns Transformed point
   */
  protected transformPoint(point: Point | { x: number; y: number }, matrix: TMat2D): Point {
    return new Point(
      matrix[0] * point.x + matrix[2] * point.y + matrix[4],
      matrix[1] * point.x + matrix[3] * point.y + matrix[5]
    );
  }

  /**
   * Checks if point is inside polygon
   * @param point - Point to check
   * @param polygon - Array of polygon vertices
   * @returns True if point is inside
   */
  protected isPointInPolygon(point: Point, polygon: Point[]): boolean {
    let inside = false;
    const n = polygon.length;

    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = polygon[i].x;
      const yi = polygon[i].y;
      const xj = polygon[j].x;
      const yj = polygon[j].y;

      const intersect = yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

      if (intersect) inside = !inside;
    }

    return inside;
  }

  /**
   * Checks if polygon intersects with rectangle
   * @param polygon - Polygon vertices
   * @param tl - Top-left corner of rectangle
   * @param br - Bottom-right corner of rectangle
   * @returns True if intersects
   */
  protected polygonIntersectsWithRect(polygon: Point[], tl: Point, br: Point): boolean {
    // Check if any polygon point is inside rectangle
    for (const point of polygon) {
      if (point.x >= tl.x && point.x <= br.x && point.y >= tl.y && point.y <= br.y) {
        return true;
      }
    }

    const intersection = Intersection.intersectPolygonRectangle(polygon, tl, br);
    if (intersection.status === 'Intersection' || intersection.status === 'Coincident') {
      return true;
    }

    return this.isPointInPolygon(tl, polygon);
  }

  /**
   * Checks if two polygons intersect
   * @param polygon1 - First polygon
   * @param polygon2 - Second polygon
   * @returns True if intersects
   */
  protected polygonIntersectsWithPolygon(polygon1: Point[], polygon2: Point[]): boolean {
    const intersection = Intersection.intersectPolygonPolygon(polygon1, polygon2);
    return intersection.status === 'Intersection' || intersection.status === 'Coincident';
  }

  /**
   * Checks if two arrays are equal
   * @param a - First array
   * @param b - Second array
   * @returns True if arrays are equal
   */
  protected arrayEquals(a: number[], b: number[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((val, i) => val === b[i]);
  }

  // #endregion
}
